import React, { useEffect } from 'react';
import { useState } from 'react';
import { Grid, Box } from '@mui/material';
import RegisterFooter from '../components/RegisterFooter';
import ShoppingPreferences from '../components/ShoppingPreferences';
import Navbar from '../components/Navbar';
import Header from '../components/Header';
import Language from '../components/Language';
import Login from '../components/common/Login';
import RegisterFamiliplusCard from '../components/RegisterFamiliplusCard';
import CommunicationPreferences from '../components/CommunicationPreferences';
import { Auth0JWT, ProfileInfo } from '../components/common/props-types';
import { RegisterConfirmation } from 'components/RegisterConfirmation';
import { RegisterFamiliplusConfirmation } from '../components/RegisterHalfConfirmations';
import { Gender } from 'services/profile/constants';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { shadows } from 'lightTheme';
import { GetNewsletterPrefs, GetProfileInfo } from 'services/profile/ProfileService';
import { GetUserInterests } from 'services/interests/InterestService';
import { useAuth0 } from '@auth0/auth0-react';
import { jwtDecode } from 'jwt-decode';
import AppLoadingIndicator from 'components/common/AppLoadingIndicator';

const gradient = (theme: { palette: { background: { gradient: string } } }) => theme.palette.background.gradient;
const background = (theme: { palette: { background: { main: string } } }) => theme.palette.background.main;
const backgroundDark = (theme: { palette: { background: { dark: string } } }) => theme.palette.background.dark;
const backgroundLight = (theme: { palette: { background: { light: string } } }) => theme.palette.background.light;

const initUser: ProfileInfo = {
  birthday: undefined,
  language: '',
  gender: Gender.Unknown,
  lastName: '',
  firstName: '',
  email: '',
  homePhone: '',
  mobilePhone: '',
  familiplusCard: '',
  familiplusNumber: '',
  childBirthYear: [0, 0, 0, 0, 0],
  ereceiptPreference: 0,
  newsletterConsent: false,
  newsletterTypes: [],
  interests: [],
  smsConsent: false,
  oldProfileWasNewsletter: false,
};

let hasBeenUpdated = false;
let isFamiliplusProfileUpdated = false;

const FormController = (props: {
  currentStep: number;
  stepHandler: (value: number | ((prevVar: number) => number)) => void;
}) => {
  const { isAuthenticated, loginWithRedirect, getAccessTokenSilently } = useAuth0();
  const [authProvider, setAuthProvider] = useState({ sessionId: '', token: '' });
  const [profile, setProfileInfo] = useState<ProfileInfo>(initUser);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const getProfilIfExists = async () => {
    if (hasBeenUpdated == false) {
      const getprofileInfo = await GetProfileInfo(authProvider);
      const userInterests = await GetUserInterests(authProvider.sessionId);
      const newsletterPrefResponse = await GetNewsletterPrefs(authProvider.sessionId);
      setProfileInfo({
        ...profile,
        firstName: getprofileInfo.firstName,
        lastName: getprofileInfo.lastName,
        email: getprofileInfo.email,
        mobilePhone: getprofileInfo.mobilePhone,
        homePhone: getprofileInfo.homePhone,
        birthday: getprofileInfo.birthday,
        gender: getprofileInfo.gender,
        ereceiptPreference: getprofileInfo.ereceiptPreference,
        familiplusCard: getprofileInfo.familiplusCard,
        familiplusNumber: getprofileInfo.familiplusNumber,
        newsletterConsent: getprofileInfo.newsletterConsent,
        smsConsent: getprofileInfo.smsConsent,
        profilingConsent: getprofileInfo.profilingConsent,
        language: getprofileInfo.language,
        interests: userInterests.data.Interests.map((interest: any) => interest.Id),
        newsletterTypes: newsletterPrefResponse?.data?.NewsletterType ?? [],
        oldProfileWasNewsletter: getprofileInfo.newsletterConsent!,
      });

      hasBeenUpdated = true;
      setIsLoading(false);
    }
  };

  const UpdateFamiliplusProfile = async () => {
    if (isFamiliplusProfileUpdated == false) {
      const getprofileInfo = await GetProfileInfo(authProvider);
      setProfileInfo({
        ...profile,
        ereceiptPreference: getprofileInfo.ereceiptPreference,
        familiplusCard: getprofileInfo.familiplusCard,
        familiplusNumber: profile.mobilePhone || profile.homePhone,
        familiplusCardPoints: getprofileInfo.familiplusCardPoints,
      });

      isFamiliplusProfileUpdated = true;
    }
  };

  useEffect(() => {
    const GetToken = async () => {
      if (isAuthenticated) {
        const token = await getAccessTokenSilently();
        if (token) {
          const jwtInfo: Auth0JWT = jwtDecode(token);
          const { session_id } = jwtInfo;

          setAuthProvider({ sessionId: session_id, token: token });
        }
      } else {
        loginWithRedirect();
      }
    };

    setIsLoading(true);
    GetToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (authProvider.sessionId) {
      getProfilIfExists();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authProvider]);

  switch (props.currentStep) {
    case 2:
      return (
        <>
          {isLoading ? (
            <AppLoadingIndicator />
          ) : (
            <RegisterFamiliplusCard
              stepHandler={props.stepHandler}
              currentStep={props.currentStep}
              profileAuth={authProvider}
              profileHandler={setProfileInfo}
              profile={profile}
              isCreatingAccount={true}
            />
          )}
        </>
      );
    case 2.5:
      UpdateFamiliplusProfile();
      return (
        <RegisterFamiliplusConfirmation
          profile={profile}
          currentStep={props.currentStep}
          stepHandler={props.stepHandler}
        />
      );
    case 3:
      return (
        <ShoppingPreferences
          profileAuth={authProvider}
          stepHandler={props.stepHandler}
          currentStep={props.currentStep}
          profileHandler={setProfileInfo}
          profile={profile}
        />
      );
    case 4:
      return (
        <CommunicationPreferences
          stepHandler={props.stepHandler}
          currentStep={props.currentStep}
          profileAuth={authProvider}
          profileHandler={setProfileInfo}
          profile={profile}
        />
      );
    case 5:
      return <RegisterConfirmation />;
    default:
      return <> </>;
  }
};

const RegisterAccount = (): JSX.Element => {
  const [currentStep, setCurrentStep] = useState(2);
  const [fullNavbar, setMenu] = useState(false);
  const { t } = useTranslation('component');

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>{t('MetaTag.RegisterTitle')}</title>
        <meta name="description" content={t('MetaTag.RegisterDescription').toString()} />
      </Helmet>
      <Box sx={{ display: 'flex', flexFlow: 'column' }}>
        <Box sx={{ flex: '1 1 auto' }}>
          <Grid container direction={{ xs: 'column', md: 'row' }} height="100%" bgcolor={background}>
            <Box
              sx={{
                width: { xs: 'none', md: 480 },
                backgroundImage: gradient,
                color: 'white',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: shadows.lightBox,
              }}>
              <Box sx={{ p: { xs: '33px 0', md: '33px 48px' } }} bgcolor={backgroundLight}>
                <Header fullNavbar={fullNavbar} navbarHandler={setMenu} />
              </Box>
              <Box sx={{ p: { xs: 'none', md: '5px 0 15px 0' } }}>
                <Navbar index={currentStep} fullNavbar={fullNavbar} />
              </Box>

              <Box
                sx={{
                  p: { xs: 'none', md: '33px 48px' },
                  display: { xs: 'none', md: 'flex' },
                  fontWeight: '300',
                  flexDirection: 'column',
                  height: 'auto',
                  marginTop: 'auto',
                  zIndex: 1,
                }}>
                {currentStep == 1 && <Box>{<Login />}</Box>}

                <Language variant="menuSecondary" />
              </Box>
              <Box sx={{ position: 'relative' }}>
                <svg
                  className={`familiplusLogoBottom ${fullNavbar ? '' : 'familiplusLogoBottomMobileNotFullNavbar'}`}
                  width="425"
                  height="425"
                  viewBox="0 0 353 354"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    opacity="0.08"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M60.5796 271.471C60.5796 388.069 156.581 482.956 274.597 482.956C392.598 482.956 488.6 388.069 488.6 271.471C488.6 154.843 392.598 59.9711 274.597 59.9711C156.581 59.9711 60.5796 154.843 60.5796 271.471ZM0 271.471C0 121.594 122.936 0.10498 274.597 0.10498C426.258 0.10498 549.194 121.594 549.194 271.471C549.194 421.334 426.258 542.838 274.597 542.838C122.936 542.838 0 421.334 0 271.471ZM205.011 348.272H352.472V202.532C352.472 179.107 333.175 160.052 309.442 160.052C285.725 160.052 266.427 179.107 266.427 202.517V263.313H205.011C181.293 263.313 161.967 282.368 161.967 305.778C161.967 329.217 181.293 348.272 205.011 348.272ZM215.07 202.516C215.07 151.125 257.395 109.313 309.442 109.313C361.474 109.313 403.814 151.125 403.814 202.516V373.641C403.814 387.631 392.3 399.01 378.143 399.01H205.011C152.979 399.01 110.639 357.183 110.639 305.777C110.639 254.386 152.979 212.574 205.011 212.574H215.07V202.516Z"
                    fill="white"
                  />
                </svg>
              </Box>
            </Box>
            <Box
              sx={{
                p: { xs: '16px', md: '0' },
                width: { xs: 'none', md: 512 },
                margin: { xs: '16px auto', md: '56px auto' },
              }}>
              <FormController currentStep={currentStep} stepHandler={setCurrentStep} />
            </Box>
          </Grid>
        </Box>
        <Box sx={{ flex: '0 1 149px', zIndex: 1 }}>
          <Grid item xs={12} bgcolor={backgroundDark}>
            <RegisterFooter />
          </Grid>
        </Box>
      </Box>
    </>
  );
};

export default RegisterAccount;
